<template>
  <div class="min-h-screen flex flex-col bg-white text-fg font-sans">
    <!-- Dynamic Header -->
    <header
      data-testid="header"
      class="fixed top-0 left-0 right-0 z-40 pt-safe backdrop-blur-xl backdrop-saturate-150 border-b transition-all duration-300"
      :class="isScrolled ? 'bg-white/85 border-line/60 shadow-sm' : 'bg-[#05060f]/40 border-white/10'"
    >
      <div class="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <a
          data-testid="link-logo"
          class="flex items-center gap-2 cursor-pointer"
          href="#"
          @click.prevent="scrollToSection('hero')"
        >
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </a>

        <nav class="hidden md:flex items-center gap-6">
          <button
            data-testid="nav-서비스"
            class="text-sm font-medium transition-colors cursor-pointer"
            :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/85 hover:text-white'"
            @click="scrollToSection('services')"
          >
            서비스
          </button>
          <button
            data-testid="nav-혜택"
            class="text-sm font-medium transition-colors cursor-pointer"
            :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/85 hover:text-white'"
            @click="scrollToSection('benefits')"
          >
            혜택
          </button>
          <button
            data-testid="nav-성과"
            class="text-sm font-medium transition-colors cursor-pointer"
            :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/85 hover:text-white'"
            @click="scrollToSection('performance')"
          >
            성과
          </button>
          <button
            data-testid="nav-진행 방식"
            class="text-sm font-medium transition-colors cursor-pointer"
            :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/85 hover:text-white'"
            @click="scrollToSection('process')"
          >
            진행 방식
          </button>
          <button
            data-testid="nav-FAQ"
            class="text-sm font-medium transition-colors cursor-pointer"
            :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/85 hover:text-white'"
            @click="scrollToSection('faq')"
          >
            FAQ
          </button>
        </nav>

        <div class="flex items-center gap-3">
          <button
            data-testid="button-header-cta"
            class="hidden md:inline-flex items-center px-5 py-2.5 rounded-[12px] border text-sm font-semibold backdrop-blur-sm transition-colors cursor-pointer"
            :class="isScrolled ? 'border-line bg-white/60 text-fg hover:bg-white' : 'border-white/30 bg-white/10 text-white hover:bg-white/20'"
            @click="openModal"
          >
            무료 상담 신청
          </button>
          <button
            data-testid="button-mobile-menu"
            class="md:hidden -mr-2 flex items-center justify-center min-w-[44px] min-h-[44px] transition-colors cursor-pointer"
            :class="isScrolled ? 'text-fg' : 'text-white'"
            aria-label="메뉴 열기"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-menu"
              aria-hidden="true"
            >
              <path d="M4 5h16" />
              <path d="M4 12h16" />
              <path d="M4 19h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Nav Drawer -->
      <div
        v-if="isMobileMenuOpen"
        class="md:hidden border-b px-4 py-4 space-y-3 shadow-md transition-colors"
        :class="isScrolled ? 'bg-white border-line text-fg' : 'bg-[#05060f] border-white/10 text-white'"
      >
        <button
          class="block w-full text-left py-2 text-sm font-medium transition-colors"
          :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/90 hover:text-white'"
          @click="scrollToSection('services')"
        >
          서비스
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium transition-colors"
          :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/90 hover:text-white'"
          @click="scrollToSection('benefits')"
        >
          혜택
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium transition-colors"
          :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/90 hover:text-white'"
          @click="scrollToSection('performance')"
        >
          성과
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium transition-colors"
          :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/90 hover:text-white'"
          @click="scrollToSection('process')"
        >
          진행 방식
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium transition-colors"
          :class="isScrolled ? 'text-fg-muted hover:text-fg' : 'text-white/90 hover:text-white'"
          @click="scrollToSection('faq')"
        >
          FAQ
        </button>
        <button data-testid="button-header-cta" class="w-full py-3 rounded-xl bg-brand-500 text-white font-bold text-sm mt-2" @click="openModal">무료 상담 신청</button>
      </div>
    </header>

    <main class="flex-1">
      <!-- 1. Hero Section -->
      <section id="hero" class="relative min-h-[100svh] bg-[#05060f] pt-16 overflow-hidden flex flex-col justify-center">
        <div class="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_rgba(242,178,42,0.38),_transparent_65%)]"></div>
        <div class="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-b from-transparent via-transparent to-[#05060f]"></div>

        <div class="relative z-10 flex-1 flex items-center w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div class="w-full max-w-5xl mx-auto text-center">
            <h1 class="hidden md:block text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-snug mb-5 whitespace-pre-line">
              매장은 웨이팅, 쇼핑몰은 품절.<br />52만 인플루언서가 매출을 만듭니다.
            </h1>
            <h1 class="md:hidden text-[1.75rem] font-bold text-white leading-snug mb-4 whitespace-pre-line">
              매장은 웨이팅, 쇼핑몰은 품절<br />52만 인플루언서가 만듭니다
            </h1>

            <p class="text-base md:text-lg text-white/75 leading-relaxed mb-8 max-w-2xl mx-auto">
              <span>맞춤 1:1 설계로 시작하세요.</span>
            </p>

            <div class="flex flex-wrap justify-center gap-2 mb-8">
              <span class="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm">블로그</span>
              <span class="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm">인스타그램</span>
              <span class="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm">플레이스</span>
              <span class="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm">스마트 스토어</span>
              <span class="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm">자사몰</span>
              <span class="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium backdrop-blur-sm">앱</span>
            </div>

            <div class="grid transition-[grid-template-rows] duration-700 ease-out grid-rows-[1fr]">
              <div class="overflow-hidden">
                <div class="pt-10 max-w-3xl mx-auto transition-[opacity,transform] duration-700 ease-out opacity-100 translate-y-0">
                  <div class="star-border-container w-full shadow-[0_8px_32px_rgba(242,178,42,0.4)] p-[3px] rounded-[17px] bg-gradient-to-r from-brand-500 to-warning">
                    <div class="star-border-inner">
                      <div class="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 rounded-[13px] bg-gradient-to-r from-brand-500 to-warning px-6 py-5 md:px-8">
                        <div class="flex-1 text-left text-white">
                          <span class="inline-block px-2.5 py-0.5 rounded-full bg-white/25 text-[11px] font-bold mb-2">이달 한정 프로모션</span>
                          <p class="text-lg md:text-xl font-extrabold leading-snug">당일 계약 시 할인<span class="font-bold"> + 숏폼 10건 무상 제공</span></p>
                          <p class="mt-1 text-xs text-white/80">패키지 구성 계약 시 적용</p>
                        </div>
                        <button
                          type="button"
                          data-testid="button-promo-cta"
                          class="shrink-0 px-6 py-3 rounded-[25px] bg-white text-brand-600 font-bold text-sm hover:bg-white/90 transition-colors cursor-pointer shadow-md"
                          @click="openModal"
                        >
                          오늘 상담받고 혜택까지 챙기기
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 2. Problem Section -->
      <section id="problem" class="py-20 md:py-28 bg-bg-soft">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">PROBLEM</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4 whitespace-pre-line">매출이 그대로라면, 문제는 '리뷰'입니다.</h2>
            <p class="text-base text-fg-muted leading-relaxed">리뷰가 약하면 소비자들은 경쟁 업체로 넘어갑니다.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-12">
            <div data-testid="problem-card-0" class="bg-white rounded-[16px] p-6 border border-line shadow-sm">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-danger/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z" stroke="#E5484D" stroke-width="2" stroke-linecap="round"></path></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 class="text-base font-semibold text-fg">검색해도 우리 매장이 잘 안 보여요</h3>
                    <span class="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium whitespace-nowrap">플레이스 · 지도</span>
                  </div>
                  <p class="text-sm text-fg-muted leading-relaxed">콘텐츠가 쌓여야 노출이 됩니다.</p>
                </div>
              </div>
            </div>

            <div data-testid="problem-card-1" class="bg-white rounded-[16px] p-6 border border-line shadow-sm">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-danger/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z" stroke="#E5484D" stroke-width="2" stroke-linecap="round"></path></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 class="text-base font-semibold text-fg">경쟁사는 후기가 많은데 우리는 부족해요</h3>
                    <span class="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium whitespace-nowrap">스토어 · 평점</span>
                  </div>
                  <p class="text-sm text-fg-muted leading-relaxed">최신 리뷰 = 매출 '공식' 입니다.</p>
                </div>
              </div>
            </div>

            <div data-testid="problem-card-2" class="bg-white rounded-[16px] p-6 border border-line shadow-sm">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-danger/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z" stroke="#E5484D" stroke-width="2" stroke-linecap="round"></path></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 class="text-base font-semibold text-fg">인스타·숏폼에 보여줄 콘텐츠가 없어요</h3>
                    <span class="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium whitespace-nowrap">SNS · 숏폼</span>
                  </div>
                  <p class="text-sm text-fg-muted leading-relaxed">mz, 젊은 층을 노려야 브랜드도 살아납니다.</p>
                </div>
              </div>
            </div>

            <div data-testid="problem-card-3" class="bg-white rounded-[16px] p-6 border border-line shadow-sm">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-danger/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M12 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z" stroke="#E5484D" stroke-width="2" stroke-linecap="round"></path></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 class="text-base font-semibold text-fg">클릭은 늘었는데 구매·방문은 그대로예요</h3>
                    <span class="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium whitespace-nowrap">전환</span>
                  </div>
                  <p class="text-sm text-fg-muted leading-relaxed">관심을 가지고 들어와도 후기가 부족하면 구매 설득에서 실패합니다.</p>
                </div>
              </div>
            </div>
          </div>

          <figure class="relative max-w-2xl mx-auto text-center pt-10">
            <span aria-hidden="true" class="absolute left-1/2 -translate-x-1/2 top-0 font-serif text-7xl leading-none text-brand-500/25 select-none">“</span>
            <blockquote class="text-lg md:text-2xl font-bold text-fg leading-relaxed">티블은 매출이 일어날 수 있는 공식을 만들어 드립니다.</blockquote>
            <span class="mt-5 inline-block h-1 w-12 rounded-full bg-brand-500"></span>
          </figure>
        </div>
      </section>

      <!-- 3. Solution Section -->
      <section id="solution" class="py-20 md:py-28 bg-white">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-3xl mx-auto text-center mb-16">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">SOLUTION</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-5 whitespace-pre-line">리뷰 대행에서 끝나지 않습니다.<br />
그 리뷰로 매출까지 만듭니다.</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            <div data-testid="solution-axis-01" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-brand-500 flex items-center justify-center"><span class="text-white text-xs font-bold">01</span></div>
                <div><p class="text-[11px] font-semibold tracking-wider uppercase text-brand-500 mb-1">리뷰 생성</p><h3 class="text-base font-semibold text-fg">검증된 리뷰어의 후기를 우리 매장에 녹여보세요.</h3></div>
              </div>
            </div>
            <div data-testid="solution-axis-02" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-brand-500 flex items-center justify-center"><span class="text-white text-xs font-bold">02</span></div>
                <div><p class="text-[11px] font-semibold tracking-wider uppercase text-brand-500 mb-1">채널 확산</p><h3 class="text-base font-semibold text-fg">어디에서 검색해도 보이게 합니다.</h3></div>
              </div>
            </div>
            <div data-testid="solution-axis-03" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-brand-500 flex items-center justify-center"><span class="text-white text-xs font-bold">03</span></div>
                <div><p class="text-[11px] font-semibold tracking-wider uppercase text-brand-500 mb-1">검색·플레이스 최적화</p><h3 class="text-base font-semibold text-fg">제대로 된 환경부터 구축해드립니다.</h3></div>
              </div>
            </div>
            <div data-testid="solution-axis-04" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-[10px] bg-brand-500 flex items-center justify-center"><span class="text-white text-xs font-bold">04</span></div>
                <div><p class="text-[11px] font-semibold tracking-wider uppercase text-brand-500 mb-1">성과 관리</p><h3 class="text-base font-semibold text-fg">모든 진행 현황을 투명하게 공개합니다.</h3></div>
              </div>
            </div>
          </div>

          <div class="text-center">
            <button
              type="button"
              data-testid="button-solution-cta"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[12px] font-bold text-base transition-colors duration-200 bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              우리 비즈니스에 맞는 조합 제안받기
            </button>
          </div>
        </div>
      </section>

      <!-- 4. Benefits Section -->
      <section id="benefits" class="relative py-16 md:py-[5.6rem] bg-bg-soft overflow-hidden">
        <div class="max-w-7xl mx-auto px-4 md:px-6 relative lg:min-h-[440px]">
          <!-- Left Column (Desktop & Mobile header) -->
          <div class="lg:max-w-[48%]">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">BENEFITS</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4 whitespace-pre-line">티블을 믿고 선택해주신 대표님들께<br />드리는 혜택입니다</h2>
            <p class="text-base text-fg-muted leading-relaxed mb-8">적용 조건은 상담 시 안내드립니다.</p>

            <button
              type="button"
              data-testid="button-benefits-cta"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[12px] font-bold text-base transition-colors duration-200 bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              우리 비즈니스 적용 가능 혜택 확인하기
            </button>
          </div>

          <!-- Mobile Cards Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10 lg:hidden">
            <div data-testid="benefit-card-0" class="relative flex flex-col bg-white rounded-[16px] p-6 border border-accent-hot/50 shadow-[0_0_0_2px_rgba(255,115,82,0.15)]">
              <span class="absolute rounded-full bg-accent-hot text-white font-bold uppercase tracking-wide top-4 right-4 px-2 py-0.5 text-[10px]">HOT</span>
              <img alt="" class="object-contain w-12 h-12 mb-3" src="/images/boon_1_active.svg" />
              <span class="font-bold uppercase tracking-widest text-brand-500 block text-[10px] mb-1.5">숏폼영상 무료</span>
              <h3 class="font-semibold text-fg leading-snug text-lg mb-2">당일 계약 시 숏폼 10건 무상 제공</h3>
              <p class="text-fg-muted leading-relaxed whitespace-pre-line text-sm">MZ 타깃이 가장 먼저 보는 릴스·숏폼 제작비 0원.<br />※ 패키지 구성 계약 시 적용</p>
            </div>

            <div data-testid="benefit-card-1" class="relative flex flex-col bg-white rounded-[16px] p-6 border border-line">
              <img alt="" class="object-contain w-12 h-12 mb-3" src="/images/boon_2_active.svg" />
              <span class="font-bold uppercase tracking-widest text-brand-500 block text-[10px] mb-1.5">리뷰 무제한</span>
              <h3 class="font-semibold text-fg leading-snug text-lg mb-2">캠페인당 인원 제한 없이</h3>
              <p class="text-fg-muted leading-relaxed whitespace-pre-line text-sm">블로그·인스타 체험단을 인원 제한 없이 운영해 콘텐츠 자산을 꾸준히 쌓습니다.</p>
            </div>

            <div data-testid="benefit-card-2" class="relative flex flex-col bg-white rounded-[16px] p-6 border border-line">
              <img alt="" class="object-contain w-12 h-12 mb-3" src="/images/boon_3_active.svg" />
              <span class="font-bold uppercase tracking-widest text-brand-500 block text-[10px] mb-1.5">블로그 기자단 지원</span>
              <h3 class="font-semibold text-fg leading-snug text-lg mb-2">제품·식사 제공 없이 원고료 100% 지원</h3>
              <p class="text-fg-muted leading-relaxed whitespace-pre-line text-sm">제공 부담 없이 원고료만으로 진행해 시작 부담을 가장 크게 줄입니다.</p>
            </div>

            <div data-testid="benefit-card-3" class="relative flex flex-col bg-white rounded-[16px] p-6 border border-line">
              <img alt="" class="object-contain w-12 h-12 mb-3" src="/images/boon_4_active.svg" />
              <span class="font-bold uppercase tracking-widest text-brand-500 block text-[10px] mb-1.5">검색노출 최적화</span>
              <h3 class="font-semibold text-fg leading-snug text-lg mb-2">플레이스·스토어·키워드 함께 점검</h3>
              <p class="text-fg-muted leading-relaxed whitespace-pre-line text-sm">리뷰 발행 전 노출 환경부터 정비해 리뷰가 발견되게 만듭니다.</p>
            </div>
          </div>
        </div>

        <!-- Desktop 3D Interactive Card Stack -->
        <div class="pointer-events-none absolute inset-0 hidden lg:block">
          <div class="relative h-full max-w-7xl mx-auto px-4 md:px-6">
            <div class="pointer-events-auto absolute inset-y-0 right-0 w-[46%] xl:w-[44%] translate-y-[80px]">
              <!-- Filter Tabs -->
              <div class="absolute right-0 top-0 z-20 flex w-[560px] translate-x-[5%] -translate-y-[15px] flex-wrap justify-center gap-2">
                <button
                  v-for="(bTab, bIdx) in benefitTabs"
                  :key="bIdx"
                  type="button"
                  :data-testid="'benefit-pick-' + bIdx"
                  class="rounded-full px-3.5 py-1.5 text-xs font-semibold backdrop-blur-sm transition-all cursor-pointer"
                  :class="activeBenefit === bIdx ? 'bg-brand-500 text-white shadow-sm' : 'bg-white/70 text-fg-muted ring-1 ring-line hover:bg-white'"
                  @click="selectBenefit(bIdx)"
                >
                  {{ bTab }}
                </button>
              </div>

              <!-- 3D Perspective Stack -->
              <div class="absolute top-1/2 right-0 origin-right -translate-y-1/2 translate-x-[5%] [perspective:900px] [transform-style:preserve-3d]" style="width: 560px; height: 340px;">
                <div
                  v-for="(card, idx) in benefitCards"
                  :key="idx"
                  :data-testid="'benefit-card-' + idx"
                  class="absolute top-1/2 left-1/2 rounded-xl [transform-style:preserve-3d] [will-change:transform] [backface-visibility:hidden] flex flex-col justify-center p-8 bg-white border overflow-hidden transition-all duration-500 cursor-pointer"
                  :class="[
                    (idx - activeBenefit + 4) % 4 === 0
                      ? (idx === 0 ? 'border-accent-hot/50 shadow-[0_28px_70px_rgba(255,115,82,0.30)]' : 'border-brand-500 shadow-[0_28px_70px_rgba(242,178,42,0.35)] ring-2 ring-brand-500/20')
                      : 'border-line shadow-[0_20px_50px_rgba(0,0,0,0.12)]'
                  ]"
                  :style="getBenefitCardStyle(idx)"
                  @click="selectBenefit(idx)"
                >
                  <span v-if="card.badge" class="absolute rounded-full bg-accent-hot text-white font-bold uppercase tracking-wide top-6 right-6 px-3 py-1 text-xs">
                    {{ card.badge }}
                  </span>
                  <img :src="card.img" alt="" class="object-contain w-20 h-20 mb-6" />
                  <span class="font-bold uppercase tracking-widest text-brand-500 block text-xs mb-2.5">{{ card.label }}</span>
                  <h3 class="font-semibold text-fg leading-snug text-2xl mb-3.5">{{ card.title }}</h3>
                  <p class="text-fg-muted leading-relaxed whitespace-pre-line text-base">{{ card.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 5. Trust & Marquee Section -->
      <section id="trust" class="py-20 md:py-28 bg-white">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">TRUST</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight whitespace-pre-line">이미 많은 브랜드가<br />
티블과 함께 매출을 키우고 있습니다</h2>
          </div>

          <div class="mb-12">
            <div data-testid="brand-marquee" class="relative overflow-hidden border border-line rounded-[20px] bg-bg-soft py-6 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
              <div class="flex items-center gap-10 animate-marquee w-max">
                <img v-for="n in 31" :key="n" :src="'/logos/brand_logo_' + n + '.svg'" alt="" class="h-10 md:h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity flex-shrink-0" />
                <img v-for="n in 31" :key="'dup-' + n" :src="'/logos/brand_logo_' + n + '.svg'" alt="" class="h-10 md:h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            <div data-testid="trust-metric-검증된 인플루언서 풀" class="text-center p-6 rounded-[16px] bg-bg-soft border border-line">
              <div class="text-2xl md:text-3xl font-bold text-brand-500 mb-1">520,000+</div>
              <div class="text-xs text-fg-muted">검증된 인플루언서 풀</div>
            </div>
            <div data-testid="trust-metric-일주일 평균 광고주 상담" class="text-center p-6 rounded-[16px] bg-bg-soft border border-line">
              <div class="text-2xl md:text-3xl font-bold text-brand-500 mb-1">250+</div>
              <div class="text-xs text-fg-muted">일주일 평균 광고주 상담</div>
            </div>
            <div data-testid="trust-metric-통합 운영 채널" class="text-center p-6 rounded-[16px] bg-bg-soft border border-line">
              <div class="text-2xl md:text-3xl font-bold text-brand-500 mb-1">6</div>
              <div class="text-xs text-fg-muted">통합 운영 채널</div>
            </div>
            <div data-testid="trust-metric-전담 매니저 배정" class="text-center p-6 rounded-[16px] bg-bg-soft border border-line">
              <div class="text-2xl md:text-3xl font-bold text-brand-500 mb-1">1:1</div>
              <div class="text-xs text-fg-muted">전담 매니저 배정</div>
            </div>
          </div>

          <!-- Bento Dashboard -->
          <div class="relative overflow-hidden bg-fg rounded-[20px] p-8 md:p-12 text-white">
            <span class="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-3 block">TRANSPARENCY</span>
            <h3 class="text-xl md:text-3xl font-bold mb-4 leading-snug whitespace-pre-line">보이지 않으면 광고가 아닙니다.<br />
모든 운영을 광고주 대시보드에서 보여드립니다.</h3>
            <div class="max-w-2xl space-y-4">
              <p class="text-base text-white/70 leading-relaxed">진행 현황·등록 리뷰·키워드 유입·조회수·리뷰어 명단까지 광고주 페이지에서 실시간 확인할 수 있습니다.</p>
              <p class="text-base text-white/70 leading-relaxed">계약 후 사후관리까지 투명하게 보여드립니다.</p>
            </div>

            <div class="relative mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <div class="relative overflow-hidden rounded-[16px] bg-white/[0.06] border border-white/10 p-5 flex flex-col sm:col-span-2 lg:row-span-2">
                <div class="flex items-center justify-between">
                  <h4 class="text-sm font-semibold text-white/90">캠페인 진행 현황</h4>
                  <span class="flex items-center gap-1.5 text-[10px] font-medium text-white/45">최근 8주</span>
                </div>
                <div class="mt-4 flex flex-1 flex-col">
                  <svg viewBox="0 0 300 110" class="w-full flex-1 min-h-[7rem]" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="bento-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="rgb(242,178,42)" stop-opacity="0.45" />
                        <stop offset="100%" stop-color="rgb(242,178,42)" stop-opacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,82 C24,74 40,56 64,60 C92,65 104,30 132,36 C162,42 178,20 212,26 C248,32 264,12 300,18 L300,110 L0,110 Z" fill="url(#bento-area)"></path>
                    <path d="M0,82 C24,74 40,56 64,60 C92,65 104,30 132,36 C162,42 178,20 212,26 C248,32 264,12 300,18" fill="none" stroke="rgb(242,178,42)" stroke-width="2.5" vector-effect="non-scaling-stroke"></path>
                  </svg>
                  <div class="mt-1 flex justify-between text-[10px] text-white/40"><span>1주</span><span>3주</span><span>5주</span><span>7주</span></div>
                </div>
                <div class="mt-auto grid grid-cols-3 gap-3 pt-5">
                  <div>
                    <div class="text-lg font-bold text-white tabular-nums">312</div>
                    <div class="mb-1.5 text-[10px] text-white/45">발행 완료</div>
                    <div class="h-1 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-brand-500" style="width: 78%;"></div></div>
                  </div>
                  <div>
                    <div class="text-lg font-bold text-white tabular-nums">46</div>
                    <div class="mb-1.5 text-[10px] text-white/45">진행 중</div>
                    <div class="h-1 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-brand-500" style="width: 32%;"></div></div>
                  </div>
                  <div>
                    <div class="text-lg font-bold text-white tabular-nums">20</div>
                    <div class="mb-1.5 text-[10px] text-white/45">예정</div>
                    <div class="h-1 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-brand-500" style="width: 14%;"></div></div>
                  </div>
                </div>
              </div>

              <div class="relative overflow-hidden rounded-[16px] hidden sm:block bg-white/[0.06] border border-white/10 p-5">
                <div class="flex items-center justify-between"><h4 class="text-sm font-semibold text-white/90">이번 달 등록 리뷰</h4></div>
                <div class="mt-3 flex items-end gap-2"><span class="text-3xl font-bold text-white tabular-nums">1,284</span><span class="mb-1 text-xs font-semibold text-emerald-400">▲ 18.2%</span></div>
                <svg viewBox="0 0 200 50" class="mt-4 h-12 w-full" preserveAspectRatio="none">
                  <polyline points="0,40 25,32 50,36 75,22 100,28 125,15 150,20 175,8 200,12" fill="none" stroke="rgb(52,211,153)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"></polyline>
                </svg>
              </div>

              <div class="relative overflow-hidden rounded-[16px] bg-white/[0.06] border border-white/10 p-5">
                <div class="flex items-center justify-between"><h4 class="text-sm font-semibold text-white/90">검색 노출 순위</h4><span class="flex items-center gap-1.5 text-[10px] font-medium text-white/45">일간</span></div>
                <div class="mt-4 space-y-2.5">
                  <div class="flex items-center gap-2.5"><span class="grid h-7 w-7 flex-shrink-0 place-items-center rounded-[8px] bg-brand-500/20 text-xs font-bold text-brand-500 tabular-nums">2위</span><div class="min-w-0 flex-1"><p class="truncate text-xs text-white/80">지역+업종 키워드</p><p class="text-[10px] text-white/40">플레이스</p></div><span class="text-[10px] font-semibold tabular-nums text-emerald-400">▲ 3</span></div>
                  <div class="flex items-center gap-2.5"><span class="grid h-7 w-7 flex-shrink-0 place-items-center rounded-[8px] bg-brand-500/20 text-xs font-bold text-brand-500 tabular-nums">1위</span><div class="min-w-0 flex-1"><p class="truncate text-xs text-white/80">브랜드 키워드</p><p class="text-[10px] text-white/40">블로그</p></div><span class="text-[10px] font-semibold tabular-nums text-white/40">—</span></div>
                  <div class="flex items-center gap-2.5"><span class="grid h-7 w-7 flex-shrink-0 place-items-center rounded-[8px] bg-brand-500/20 text-xs font-bold text-brand-500 tabular-nums">4위</span><div class="min-w-0 flex-1"><p class="truncate text-xs text-white/80">상품 키워드</p><p class="text-[10px] text-white/40">검색</p></div><span class="text-[10px] font-semibold tabular-nums text-emerald-400">▲ 2</span></div>
                </div>
              </div>

              <div class="relative overflow-hidden rounded-[16px] hidden sm:block bg-white/[0.06] border border-white/10 p-5 sm:col-span-2">
                <div class="flex items-center justify-between"><h4 class="text-sm font-semibold text-white/90">키워드 유입 추이</h4><span class="flex items-center gap-1.5 text-[10px] font-medium text-white/45">주간</span></div>
                <div class="mt-4">
                  <svg viewBox="0 0 320 100" class="h-24 w-full" preserveAspectRatio="none">
                    <polyline points="0,70 45,55 90,60 135,38 180,45 225,25 270,30 320,15" fill="none" stroke="rgb(242,178,42)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"></polyline>
                    <polyline points="0,85 45,80 90,72 135,75 180,60 225,65 270,50 320,52" fill="none" stroke="rgba(125,211,252,0.85)" stroke-width="2" stroke-dasharray="4 4" stroke-linecap="round" vector-effect="non-scaling-stroke"></polyline>
                  </svg>
                  <div class="mt-2 flex gap-4 text-[10px]">
                    <span class="flex items-center gap-1.5 text-white/60"><i class="inline-block h-2 w-2 rounded-full bg-brand-500"></i>브랜드 키워드</span>
                    <span class="flex items-center gap-1.5 text-white/60"><i class="inline-block h-2 w-2 rounded-full bg-sky-300"></i>상품 키워드</span>
                  </div>
                </div>
              </div>

              <div class="relative overflow-hidden rounded-[16px] bg-white/[0.06] border border-white/10 p-5 sm:col-span-2">
                <div class="flex items-center justify-between"><h4 class="text-sm font-semibold text-white/90">채널별 조회수</h4></div>
                <div class="mt-4 space-y-3">
                  <div>
                    <div class="mb-1 flex justify-between text-[11px]"><span class="text-white/70">블로그</span><span class="tabular-nums text-white/50">1.2M</span></div>
                    <div class="h-2 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-gradient-to-r from-brand-500/50 to-brand-500" style="width: 92%;"></div></div>
                  </div>
                  <div>
                    <div class="mb-1 flex justify-between text-[11px]"><span class="text-white/70">인스타그램</span><span class="tabular-nums text-white/50">980K</span></div>
                    <div class="h-2 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-gradient-to-r from-brand-500/50 to-brand-500" style="width: 74%;"></div></div>
                  </div>
                  <div>
                    <div class="mb-1 flex justify-between text-[11px]"><span class="text-white/70">플레이스</span><span class="tabular-nums text-white/50">610K</span></div>
                    <div class="h-2 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-gradient-to-r from-brand-500/50 to-brand-500" style="width: 58%;"></div></div>
                  </div>
                  <div>
                    <div class="mb-1 flex justify-between text-[11px]"><span class="text-white/70">스마트 스토어</span><span class="tabular-nums text-white/50">420K</span></div>
                    <div class="h-2 overflow-hidden rounded-full bg-white/10"><div class="h-full rounded-full bg-gradient-to-r from-brand-500/50 to-brand-500" style="width: 40%;"></div></div>
                  </div>
                </div>
              </div>

              <div class="relative overflow-hidden rounded-[16px] hidden sm:block bg-white/[0.06] border border-white/10 p-5 sm:col-span-2">
                <div class="flex items-center justify-between"><h4 class="text-sm font-semibold text-white/90">리뷰어 명단</h4><span class="flex items-center gap-1.5 text-[10px] font-medium text-white/45"><span class="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>실시간</span></div>
                <div class="mt-4 space-y-2.5">
                  <div class="flex items-center gap-3"><span class="grid h-8 w-8 place-items-center rounded-full text-xs font-bold bg-rose-400/25 text-rose-200">김</span><div class="min-w-0 flex-1"><p class="truncate text-xs text-white/80">리뷰어 #2841</p><p class="text-[10px] text-white/40">인스타그램 · 발행 완료</p></div><span class="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span></div>
                  <div class="flex items-center gap-3"><span class="grid h-8 w-8 place-items-center rounded-full text-xs font-bold bg-emerald-400/25 text-emerald-200">이</span><div class="min-w-0 flex-1"><p class="truncate text-xs text-white/80">리뷰어 #2839</p><p class="text-[10px] text-white/40">블로그 · 발행 완료</p></div><span class="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span></div>
                  <div class="flex items-center gap-3"><span class="grid h-8 w-8 place-items-center rounded-full text-xs font-bold bg-sky-400/25 text-sky-200">박</span><div class="min-w-0 flex-1"><p class="truncate text-xs text-white/80">리뷰어 #2835</p><p class="text-[10px] text-white/40">스마트 스토어 · 발행 완료</p></div><span class="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span></div>
                </div>
              </div>
            </div>
            <span class="pointer-events-none absolute bottom-4 right-5 text-[10px] text-white/30">이 대시보드는 예시입니다.</span>
          </div>
        </div>
      </section>

      <!-- 6. Services Section -->
      <section id="services" class="py-20 md:py-28 bg-white">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">SERVICES</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4 whitespace-pre-line">고르기만 하세요,
내 비즈니스에 맞는 검증된 리뷰</h2>
            <p class="text-base text-fg-muted leading-relaxed">업종·상품·목표에 따라 단독으로도,<br class="sm:hidden" /><span class="hidden sm:inline"> </span>조합으로도 운영하는 6개 라인업.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            <div v-for="(srv, idx) in serviceCards" :key="idx" :data-testid="'service-card-' + srv.id" class="bg-bg-soft rounded-[16px] p-6 border border-line hover:border-brand-200 hover:shadow-md transition-all group flex flex-col">
              <div class="flex items-center justify-between mb-4">
                <img :src="srv.img" alt="" class="w-20 h-20 md:w-24 md:h-24 object-contain -ml-1" />
                <span class="text-[10px] font-bold uppercase tracking-widest text-fg-subtle">SERVICE 0{{ idx + 1 }}</span>
              </div>
              <h3 class="text-lg font-semibold text-fg mb-2">{{ srv.title }}</h3>
              <p class="text-sm text-fg-muted mb-4">{{ srv.desc }}</p>
              <div class="mb-4">
                <p class="text-[11px] font-semibold text-fg-muted uppercase tracking-wide mb-2">추천 대상</p>
                <ul class="space-y-1">
                  <li v-for="(target, tIdx) in srv.targets" :key="tIdx" class="text-xs text-fg-muted flex items-start gap-1.5">
                    <span class="mt-1 w-1 h-1 rounded-full bg-brand-200 flex-shrink-0"></span>{{ target }}
                  </li>
                </ul>
              </div>
              <div class="mb-5">
                <p class="text-[11px] font-semibold text-fg-muted uppercase tracking-wide mb-2">기대 효과</p>
                <ul class="space-y-1">
                  <li v-for="(effect, eIdx) in srv.effects" :key="eIdx" class="text-xs text-brand-600 flex items-start gap-1.5">
                    <span class="mt-1 w-1 h-1 rounded-full bg-brand-500 flex-shrink-0"></span>{{ effect }}
                  </li>
                </ul>
              </div>
              <div class="mt-auto">
                <router-link
                  :to="srv.href"
                  :data-testid="'link-service-detail-' + srv.id"
                  style="border: 1px solid #f2b22a;"
                  class="w-full flex items-center justify-center gap-1.5 px-4 py-3 rounded-[12px] text-brand-600 text-sm font-semibold hover:bg-brand-50 group-hover:gap-2.5 transition-all cursor-pointer text-center"
                >
                  자세히 보기
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right" aria-hidden="true">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <div class="text-center">
            <button
              type="button"
              data-testid="button-services-cta"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[12px] font-bold text-base transition-colors duration-200 bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              어떤 조합이 좋을지 모르겠다면, 무료 상담받기
            </button>
          </div>
        </div>
      </section>

      <!-- 7. For Your Business Section -->
      <section id="for-your-business" class="py-20 md:py-28 bg-bg-soft">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mx-auto text-center mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">FOR YOUR BUSINESS</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4 whitespace-pre-line">내 업종, 내 목표에<br />
딱 맞는 조합으로 시작하세요</h2>
            <p class="text-base text-fg-muted leading-relaxed">단일 채널보다 조합이 결과를 만듭니다.<br class="sm:hidden" /><span class="hidden sm:inline"> </span>업종별 추천 조합을 확인해 보세요.</p>
          </div>

          <div class="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 mb-8">
            <button
              v-for="(tab, idx) in businessTabs"
              :key="idx"
              :data-testid="'tab-business-type-' + tab.id"
              class="px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer"
              :class="activeTab === idx ? 'bg-brand-500 text-white shadow-md' : 'bg-white border border-line text-fg-muted hover:border-brand-300'"
              @click="activeTab = idx"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="max-w-3xl mx-auto bg-white rounded-[20px] border border-line p-8 md:p-10 text-center">
            <p class="text-xs font-semibold uppercase tracking-widest text-brand-500 mb-2">{{ businessTabs[activeTab].tagline }}</p>
            <h3 class="text-xl md:text-2xl font-bold text-fg mb-4">{{ businessTabs[activeTab].title }}</h3>
            <div class="flex flex-wrap justify-center items-center gap-3 mb-6">
              <template v-for="(item, iIdx) in businessTabs[activeTab].combo" :key="iIdx">
                <div class="flex items-center gap-2">
                  <span class="px-4 py-2 rounded-[10px] bg-brand-50 border border-brand-100 text-brand-700 font-semibold text-sm">{{ item }}</span>
                  <span v-if="iIdx < businessTabs[activeTab].combo.length - 1" class="text-fg-subtle text-sm">+</span>
                </div>
              </template>
            </div>
            <p class="text-base text-fg-muted leading-relaxed mb-8">{{ businessTabs[activeTab].desc }}</p>
            <button
              type="button"
              data-testid="button-business-type-cta"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[12px] font-bold text-base transition-colors duration-200 bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              내 업종 맞춤 조합 무료로 제안받기
            </button>
          </div>
        </div>
      </section>

      <!-- 8. Performance Section -->
      <section id="performance" class="py-20 md:py-28 bg-white">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">PROOF</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4">말이 아닌 결과로 증명합니다</h2>
            <p class="text-base text-fg-muted leading-relaxed">직접 확인 가능한 결과 보고서를 보여드립니다</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            <div data-testid="proof-stat-0" class="bg-brand-50 border border-brand-100 rounded-[16px] p-6">
              <div class="text-4xl md:text-5xl font-bold text-brand-500 mb-2">312%</div>
              <div class="text-base font-semibold text-fg mb-2">매출 상승</div>
              <p class="text-sm text-fg-muted leading-relaxed mb-3">블로그·인스타·플레이스 통합 운영 광고주 사례</p>
              <p class="text-[11px] text-fg-subtle">* 표본·업종·기간은 상담 시 안내</p>
            </div>

            <div data-testid="proof-stat-1" class="bg-brand-50 border border-brand-100 rounded-[16px] p-6">
              <div class="text-4xl md:text-5xl font-bold text-brand-500 mb-2">2배+</div>
              <div class="text-base font-semibold text-fg mb-2">구매 전환율 증가</div>
              <p class="text-sm text-fg-muted leading-relaxed mb-3">구매평 캠페인 전후 스토어 전환율 비교</p>
              <p class="text-[11px] text-fg-subtle">* 측정 범위는 상담 시 안내</p>
            </div>

            <div data-testid="proof-stat-2" class="bg-brand-50 border border-brand-100 rounded-[16px] p-6">
              <div class="text-4xl md:text-5xl font-bold text-brand-500 mb-2">TOP</div>
              <div class="text-base font-semibold text-fg mb-2">검색 상위 노출 달성</div>
              <p class="text-sm text-fg-muted leading-relaxed mb-3">업종·키워드별 검색·플레이스 순위 사례 다수</p>
              <p class="text-[11px] text-fg-subtle">* 업종·키워드·기간에 따라 결과는 달라질 수 있습니다</p>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            <div data-testid="proof-case-0" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <span class="text-[11px] font-semibold text-fg-subtle mb-3 block">로컬 매장 · 블로그 + 맵리뷰</span>
              <p class="text-base font-semibold text-fg mb-3 leading-snug">'오픈 직후 막막했는데, 플레이스 순위가 올라왔어요'</p>
              <p class="text-xs text-brand-600 font-medium">플레이스 노출·매장 방문 증가 / 진행 기간 N개월</p>
            </div>

            <div data-testid="proof-case-1" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <span class="text-[11px] font-semibold text-fg-subtle mb-3 block">스마트스토어 · 구매평 + 블로그</span>
              <p class="text-base font-semibold text-fg mb-3 leading-snug">'광고비가 매출로 회수되는지 직접 확인했어요'</p>
              <p class="text-xs text-brand-600 font-medium">구매평 자산 누적, 검색 유입 증가 / 진행 기간 N개월</p>
            </div>

            <div data-testid="proof-case-2" class="bg-bg-soft rounded-[16px] p-6 border border-line">
              <span class="text-[11px] font-semibold text-fg-subtle mb-3 block">뷰티 브랜드 · 인스타 + 블로그</span>
              <p class="text-base font-semibold text-fg mb-3 leading-snug">'광고를 늦게 시작한 게 아쉬울 정도였어요'</p>
              <p class="text-xs text-brand-600 font-medium">인스타 확산·블로그 검색 노출 보강 / 진행 기간 N개월</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:w-fit sm:mx-auto mb-6">
            <router-link
              to="/cases"
              data-testid="link-cases"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] text-base bg-white text-brand-600 font-bold hover:bg-brand-50 transition-all duration-200 w-full cursor-pointer shadow-sm text-center"
              style="border: 1px solid #f2b22a;"
            >
              우리 업종 사례 더 보기 →
            </router-link>
            <button
              type="button"
              data-testid="button-proof-cta"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] font-bold text-base transition-all duration-200 w-full bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              우리 비즈니스 성과 상담받기
            </button>
          </div>
          <p class="text-center text-xs text-fg-subtle">수치는 특정 광고주 사례이며 업종·상품·기간에 따라 달라질 수 있습니다.</p>
        </div>
      </section>

      <!-- 9. Process Section -->
      <section id="process" class="py-20 md:py-28 bg-bg-soft">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">PROCESS</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4 whitespace-pre-line">처음이어도 걱정 없는 완벽한 운영 체계</h2>
            <p class="text-base text-fg-muted leading-relaxed">걱정 마세요. 모든 과정을 티블이 함께 합니다.</p>
          </div>

          <!-- Mobile view -->
          <div class="space-y-4 mb-8 lg:hidden">
            <div v-for="(st, idx) in processSteps" :key="idx" :data-testid="'process-step-0' + (idx + 1)" class="bg-white rounded-[16px] p-6 border border-line">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 relative">
                  <div class="w-16 h-16 rounded-[14px] bg-brand-50 flex items-center justify-center">
                    <img :src="st.img" alt="" class="w-9 h-9 object-contain" />
                  </div>
                  <span class="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-brand-500 text-white text-[10px] font-bold leading-none flex items-center min-w-[24px] justify-center h-[18px]">0{{ idx + 1 }}</span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 class="text-base font-semibold text-fg">{{ st.name }}</h3>
                    <span class="text-sm text-fg-muted">{{ st.sub }}</span>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div class="bg-bg-soft rounded-[10px] p-3"><p class="text-[10px] font-bold uppercase tracking-wide text-fg-subtle mb-1">사장님</p><p class="text-sm text-fg-muted">{{ st.owner }}</p></div>
                    <div class="bg-brand-50 rounded-[10px] p-3"><p class="text-[10px] font-bold uppercase tracking-wide text-brand-500 mb-1">티블</p><p class="text-sm text-brand-700">{{ st.tble }}</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop view -->
          <div class="hidden lg:grid grid-cols-5 gap-5 mb-8">
            <div v-for="(st, idx) in processSteps" :key="idx" :data-testid="'process-step-0' + (idx + 1)" class="relative flex flex-col items-center">
              <span v-if="idx < processSteps.length - 1" aria-hidden="true" class="absolute top-8 left-1/2 h-0.5 w-[calc(100%+1.25rem)] bg-brand-200"></span>
              <div class="relative z-10 mb-4">
                <div class="w-16 h-16 rounded-[14px] bg-brand-50 ring-4 ring-bg-soft flex items-center justify-center">
                  <img :src="st.img" alt="" class="w-9 h-9 object-contain" />
                </div>
                <span class="absolute -top-1.5 -right-1.5 px-1.5 rounded-full bg-brand-500 text-white text-[10px] font-bold leading-none flex items-center min-w-[24px] justify-center h-[18px]">0{{ idx + 1 }}</span>
              </div>
              <div class="w-full flex-1 bg-white rounded-[16px] p-5 border border-line text-left">
                <h3 class="text-base font-semibold text-fg mb-1">{{ st.name }}</h3>
                <p class="text-sm text-fg-muted leading-snug mb-3">{{ st.sub }}</p>
                <div class="space-y-2">
                  <div class="bg-bg-soft rounded-[10px] p-2.5"><p class="text-[10px] font-bold uppercase tracking-wide text-fg-subtle mb-0.5">사장님</p><p class="text-xs text-fg-muted leading-snug">{{ st.owner }}</p></div>
                  <div class="bg-brand-50 rounded-[10px] p-2.5"><p class="text-[10px] font-bold uppercase tracking-wide text-brand-500 mb-0.5">티블</p><p class="text-xs text-brand-700 leading-snug">{{ st.tble }}</p></div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
            <router-link
              to="/process"
              data-testid="link-process-detail"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] text-base bg-white text-brand-600 font-bold hover:bg-brand-50 transition-all duration-200 w-full sm:w-auto cursor-pointer shadow-sm text-center"
              style="border: 1px solid #f2b22a;"
            >
              진행 방식 자세히 보기 →
            </router-link>
            <button
              type="button"
              data-testid="button-process-cta"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] font-bold text-base transition-all duration-200 w-full sm:w-auto bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              우리 비즈니스에 어떻게 적용되는지 상담받기
            </button>
          </div>
        </div>
      </section>

      <!-- 10. FAQ Section -->
      <section id="faq" class="py-20 md:py-28 bg-white">
        <div class="max-w-7xl mx-auto px-4 md:px-6">
          <div class="max-w-2xl mx-auto text-center mb-12">
            <span class="text-xs font-semibold tracking-widest uppercase text-brand-500 mb-3 block">FAQ</span>
            <h2 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-4">상담 전에 가장 많이 묻는 5가지</h2>
            <p class="text-base text-fg-muted leading-relaxed">더 많은 질문은 FAQ 페이지에서. 추가 궁금증은 무료 상담으로 안내드립니다.</p>
          </div>

          <div class="max-w-3xl mx-auto space-y-3 mb-10">
            <div v-for="(item, idx) in faqItems" :key="idx" :data-testid="'faq-item-' + idx" class="bg-bg-soft rounded-[14px] border border-line overflow-hidden">
              <button
                :data-testid="'faq-toggle-' + idx"
                class="w-full flex items-center justify-between px-5 py-4 text-left gap-4 cursor-pointer"
                @click="activeFaq = activeFaq === idx ? null : idx"
              >
                <span class="text-base font-semibold text-fg">{{ item.q }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down flex-shrink-0 text-fg-muted transition-transform duration-200" :class="{ 'rotate-180': activeFaq === idx }" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <div v-show="activeFaq === idx" class="px-5 pb-4 text-sm text-fg-muted leading-relaxed">
                {{ item.a }}
              </div>
            </div>
          </div>

          <div class="flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
            <router-link
              to="/faq"
              data-testid="link-faq-more"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] text-base bg-white text-brand-600 font-bold hover:bg-brand-50 transition-all duration-200 w-full sm:w-auto cursor-pointer shadow-sm text-center"
              style="border: 1px solid #f2b22a;"
            >
              더 많은 질문 보기 →
            </router-link>
            <button
              type="button"
              data-testid="button-faq-consult"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] font-bold text-base transition-all duration-200 w-full sm:w-auto bg-brand-500 text-white hover:bg-brand-600 cursor-pointer shadow-md"
              @click="openModal"
            >
              우리 상황으로 정확히 상담받기
            </button>
          </div>
        </div>
      </section>

      <!-- 11. Final CTA Section (Gold Background) -->
      <section id="start" class="py-20 md:py-28 bg-brand-500">
        <div class="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span class="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-4 block">START</span>
          <h2 class="text-2xl md:text-4xl font-bold text-white leading-tight mb-5 whitespace-pre-line">광고비를 매출로 바꿀 시간,
지금입니다.</h2>
          <p class="text-base text-white/75 mb-10 max-w-xl mx-auto leading-relaxed">전담 매니저가 맞춤 채널 조합과<br class="sm:hidden" /><span class="hidden sm:inline"> </span>예상 성과를 무료로 안내합니다.</p>
          <div class="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center mb-8">
            <button
              type="button"
              data-testid="button-final-cta-primary"
              class="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] font-bold text-base transition-colors duration-200 w-full sm:w-auto bg-white text-brand-600 hover:bg-white/95 cursor-pointer shadow-lg"
              @click="openModal"
            >
              무료 상담 신청하기
            </button>
            <a
              href="https://pf.kakao.com"
              target="_blank"
              data-testid="button-final-cta-kakao"
              class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] bg-transparent text-white font-bold text-base hover:bg-white/10 transition-all cursor-pointer"
              style="border: 1px solid #fff;"
            >
              <img alt="" class="w-5 h-5 brightness-0 invert" src="/images/kakao.svg" />카카오톡 실시간 상담
            </a>
            <!-- <a
              href="tel:1833-5985"
              data-testid="button-final-cta-phone"
              class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[16px] bg-transparent text-white font-bold text-base hover:bg-white/10 transition-all cursor-pointer text-center"
              style="border: 1px solid #fff;"
            >
              전화 문의
            </a> -->
          </div>
          <div class="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <span class="text-xs text-white/60">상담시간 10:00 ~ 18:00 · 토·일·공휴일 휴무</span>
            <span class="text-xs text-white/60">영업일 기준 24시간 내 1:1 회신</span>
            <span class="text-xs text-white/60">개인정보는 상담 목적으로만 사용되며 6개월 후 파기됩니다</span>
          </div>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <footer class="bg-fg text-white/70 pt-12 pb-28 md:pb-12">
      <div class="max-w-7xl mx-auto px-4 md:px-6">
        <div class="mb-8 flex items-center gap-3">
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </div>
        <p class="text-sm text-white/50 mb-8 -mt-4">다채널 리뷰 마케팅으로 매출을 올리는 광고주 서비스</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs leading-relaxed mb-8 text-white/50">
          <div class="space-y-1">
            <p>상호명: 주식회사 쓰리얼라이언스</p>
            <p>대표이사: 성동효</p>
            <p>주소: 서울시 강남구 학동로 44길 16, 1F</p>
            <p>사업자등록번호: 217-81-47577</p>
            <p>통신판매등록번호: 제2025-서울강남-04400호</p>
          </div>
          <div class="space-y-1">
            <p>개인정보 보호 최고책임자: 성동효</p>
            <p>이메일: help@3alliance.co.kr</p>
            <!-- <p>대표전화: 1833-5985</p> -->
            <!-- <p>FAX: 0504-334-6358</p> -->
            <p>상담시간: 10:00 ~ 18:00 · 토·일·공휴일 휴무</p>
          </div>
        </div>

        <div class="border-t border-white/10 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-1 md:gap-4">
          <p class="text-xs text-white/40 py-2">© 2026 주식회사 쓰리얼라이언스. All rights reserved.</p>
          <div class="flex gap-4 -my-1">
            <a href="#" data-testid="footer-link-개인정보 처리방침" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>개인정보 처리방침</a>
            <a href="#" data-testid="footer-link-이용약관" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>이용약관</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Floating Contact Button -->
    <button
      type="button"
      data-testid="button-floating-contact"
      aria-label="무료 상담 신청"
      class="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-[9998] flex items-center gap-2 pl-4 pr-5 h-14 rounded-full bg-brand-500 text-white font-semibold text-sm shadow-[0_8px_24px_rgba(242,178,42,0.45)] hover:bg-brand-600 hover:shadow-[0_12px_28px_rgba(242,178,42,0.55)] transition-all hover:scale-105 cursor-pointer"
      @click="toggleModal"
    >
      <span class="flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
        <img src="/images/headset.svg" alt="" class="w-4 h-4 brightness-0 invert" />
      </span>
      <span>문의하기</span>
    </button>

    <!-- Consultation Widget Modal -->
    <ConsultationModal :is-open="isModalOpen" @close="isModalOpen = false" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import ConsultationModal from '../common/ConsultationModal.vue';

const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);
const isModalOpen = ref(false);
const activeTab = ref(0);
const activeFaq = ref(null);
const activeBenefit = ref(0);

const benefitTabs = ['숏폼영상 무료', '리뷰 무제한', '블로그 기자단 지원', '검색노출 최적화'];
let benefitTimer = null;

const startBenefitTimer = () => {
  if (benefitTimer) clearInterval(benefitTimer);
  benefitTimer = setInterval(() => {
    activeBenefit.value = (activeBenefit.value + 1) % 4;
  }, 3000);
};

const selectBenefit = (idx) => {
  activeBenefit.value = idx;
  startBenefitTimer();
};

const benefitCards = [
  {
    label: '숏폼영상 무료',
    title: '당일 계약 시 숏폼 10건 무상 제공',
    desc: 'MZ 타깃이 가장 먼저 보는 릴스·숏폼 제작비 0원.\n※ 패키지 구성 계약 시 적용',
    badge: 'HOT',
    img: '/images/boon_1_active.svg',
  },
  {
    label: '리뷰 무제한',
    title: '캠페인당 인원 제한 없이',
    desc: '블로그·인스타 체험단을 인원 제한 없이 운영해 콘텐츠 자산을 꾸준히 쌓습니다.',
    badge: null,
    img: '/images/boon_2_active.svg',
  },
  {
    label: '블로그 기자단 지원',
    title: '제품·식사 제공 없이 원고료 100% 지원',
    desc: '제공 부담 없이 원고료만으로 진행해 시작 부담을 가장 크게 줄입니다.',
    badge: null,
    img: '/images/boon_3_active.svg',
  },
  {
    label: '검색노출 최적화',
    title: '플레이스·스토어·키워드 함께 점검',
    desc: '리뷰 발행 전 노출 환경부터 정비해 리뷰가 발견되게 만듭니다.',
    badge: null,
    img: '/images/boon_4_active.svg',
  },
];

const getBenefitCardStyle = (index) => {
  const dist = (index - activeBenefit.value + 4) % 4;
  const zIndex = 10 - dist;
  const offsetX = dist * 58;
  const offsetY = -dist * 58;
  const offsetZ = -dist * 87;
  return {
    width: '560px',
    height: '340px',
    zIndex,
    transformOrigin: '50% 50%',
    transform: `translate(-50%, -50%) translate3d(${offsetX}px, ${offsetY}px, ${offsetZ}px) skew(0deg, 5deg)`,
    transition: 'all 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
  };
};

const marketingLogIdx = ref(null);
const dwellTimer = ref(null);

const recordMarketingLog = async () => {
  if (typeof window === 'undefined') return;

  // ── 재방문 체크 ──────────────────────────────────────────
  const VISITOR_KEY = 'tble_visitor_id';
  const VISIT_COUNT_KEY = 'tble_visit_count';
  let visitorId = localStorage.getItem(VISITOR_KEY);
  let isReturn = false;

  // 방문 횟수 증가
  let visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) + 1;
  localStorage.setItem(VISIT_COUNT_KEY, String(visitCount));

  if (visitorId) {
    // 이미 방문한 적 있음 → 재방문
    isReturn = true;
  } else {
    // 최초 방문 → 고유 ID 생성 후 저장
    visitorId = 'v_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
    localStorage.setItem(VISITOR_KEY, visitorId);
  }
  // ────────────────────────────────────────────────────────

  const urlParams = new URLSearchParams(window.location.search);
  const payload = {
    site_id: 'tblebiz',
    referer: document.referrer,
    landing_url: window.location.href,
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || '',
    visitor_id: visitorId,
    is_return: isReturn ? 'Y' : 'N',
    visit_count: visitCount,
  };

  try {
    const res = await fetch('https://api.3alliance.co.kr/randBox/api/referrer.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (data.result === 'SUCCESS' && data.idx) {
      marketingLogIdx.value = data.idx;
      
      // 체류시간 1초마다 업데이트 활성화
      if (dwellTimer.value) clearInterval(dwellTimer.value);
      dwellTimer.value = setInterval(() => {
        if (marketingLogIdx.value) {
          fetch('https://api.3alliance.co.kr/randBox/api/referrer.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ idx: marketingLogIdx.value }),
          });
        }
      }, 1000);
    }
  } catch (e) {
    console.error('Marketing log error:', e);
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

onMounted(() => {
  recordMarketingLog();
  window.addEventListener('scroll', handleScroll);
  handleScroll();
  startBenefitTimer();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  if (benefitTimer) clearInterval(benefitTimer);
  if (dwellTimer.value) clearInterval(dwellTimer.value);
});

const scrollToSection = (id) => {
  isMobileMenuOpen.value = false;
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

const openModal = () => {
  isModalOpen.value = true;
};

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};

const serviceCards = [
  {
    id: 'blog',
    img: '/images/review_1.svg',
    title: '블로그 체험단·기자단',
    desc: '네이버 검색에서 우리 브랜드를 만나게 합니다',
    targets: ['지역 매장, 프랜차이즈 지점', '검색 유입이 중요한 제품', '네이버 상위 노출이 필요한 브랜드'],
    effects: ['키워드 블로그 콘텐츠 누적', '검색·플레이스 신뢰도 강화', '광고 외 자연 유입 보강'],
    href: '/blog',
  },
  {
    id: 'instagram',
    img: '/images/review_2.svg',
    title: '인스타그램 체험단·기자단',
    desc: '사용 장면과 분위기로 잠재 고객을 설득합니다',
    targets: ['MZ 타깃 브랜드, 라이프스타일 제품', '비주얼이 중요한 카페·뷰티·숙박', '신상품 인지가 필요한 브랜드'],
    effects: ['피드·릴스·숏폼 확보', 'SNS 확산과 시각적 신뢰', '신규 고객 인지 상승'],
    href: '/instagram',
  },
  {
    id: 'purchase-review',
    img: '/images/review_3.svg',
    title: '구매평 후기',
    desc: '장바구니 앞에서 망설이지 않게 합니다',
    targets: ['스마트스토어·자사몰 운영자', '상세 전환율 개선이 필요한 상품', '가격 비교가 치열한 카테고리'],
    effects: ['스토어 구매평 자산 확보', '텍스트·포토·동영상 다양화', '구매 직전 신뢰 강화'],
    href: '/purchase-review',
  },
  {
    id: 'map-review',
    img: '/images/review_4.svg',
    title: '맵리뷰',
    desc: '검색하고 지도 본 손님을 매장까지 데려옵니다',
    targets: ['식당·카페·뷰티·병원·학원', '지도·플레이스 리뷰가 적은 매장', '지역 키워드 검색이 핵심인 업종'],
    effects: ['네이버 영수증·구글·다음지도 리뷰', '방문 인증 기반 신뢰', '지역 검색 노출 보강'],
    href: '/map-review',
  },
  {
    id: 'app-review',
    img: '/images/review_5.svg',
    title: '앱 리뷰',
    desc: '설치 직전 평점·후기 벽을 넘게 합니다',
    targets: ['신규 출시 앱, 평점 개선이 필요한 앱', '설치 전 신뢰 확보가 필요한 서비스', '글로벌·국내 마켓 동시 운영 앱'],
    effects: ['App Store·Google Play 평점 보강', '실사용자 후기형 콘텐츠 확보', '설치 전환 보조'],
    href: '/app-review',
  },
  {
    id: 'shortform',
    img: '/images/review_2.svg',
    title: '숏폼 (릴스 / 클립 / 쇼츠 / 틱톡)',
    desc: '짧고 강한 영상으로 고객의 관심을 구매로 연결합니다',
    targets: ['맛집·병원·학원·피트니스 등 체험형 서비스', '화장품·뷰티·패션·식품·생활용품 브랜드', '신규 런칭·바이럴이 필요한 브랜드'],
    effects: ['숏폼 알고리즘 노출 확대', 'SNS 확산·브랜드 인지도 상승', '구매 전환·문의 증가'],
    href: '/instagram',
  },
];

const businessTabs = [
  {
    id: 'offline',
    label: '오프라인 매장 추천 조합',
    tagline: '검색 → 지도 → 방문까지 한 번에',
    title: '오프라인 매장 추천 조합',
    combo: ['영수증 리뷰', '블로그 체험단', '카카오맵 리뷰'],
    desc: '방문 인증 리뷰 + 검색용 블로그 + 시각적 인스타를 함께 운영합니다.',
  },
  {
    id: 'online',
    label: '스마트 스토어',
    tagline: '클릭한 손님이 장바구니까지 가도록',
    title: '스마트 스토어 추천 조합',
    combo: ['구매평 후기', '블로그 체험단', '순위 상승 작업'],
    desc: '스토어 구매평을 쌓으며 블로그·인스타로 검색 유입과 신뢰를 만듭니다.',
  },
  {
    id: 'brand',
    label: '브랜드 / 기업 추천 조합',
    tagline: '인지부터 사용 경험 확산까지',
    title: '브랜드 / 기업 추천 조합',
    combo: ['인스타그램 체험단', '블로그 기자단', '숏폼 제작'],
    desc: '인스타로 사용 장면, 블로그로 상세 후기, 구매평으로 신뢰를 채웁니다.',
  },
  {
    id: 'app',
    label: '앱·서비스',
    tagline: '평점·후기·신뢰를 한꺼번에',
    title: '앱·서비스 추천 조합',
    combo: ['앱 리뷰', '인스타그램 체험단', '블로그 체험단'],
    desc: '마켓 평점과 SNS 인지를 함께 키워 설치 직전 의심을 줄입니다.',
  },
];

const processSteps = [
  {
    name: '상담',
    sub: '비즈니스와 목표를 함께 확인',
    owner: '현재 상황·고민 공유',
    tble: '채널·키워드 진단',
    img: '/images/onboarding_1.svg',
  },
  {
    name: '맞춤 제안',
    sub: '채널 조합과 운영 범위 설계',
    owner: '제안 확인',
    tble: '캠페인 설계서·예산안 작성',
    img: '/images/onboarding_2.svg',
  },
  {
    name: '체험단 모집',
    sub: '52만 풀에서 영향력 기준 매칭',
    owner: '리뷰어 명단 확인',
    tble: '신청 검수·선정',
    img: '/images/onboarding_3.svg',
  },
  {
    name: '리뷰 업로드',
    sub: '콘텐츠 제작·검수·게시·노출 관리',
    owner: '승인',
    tble: '가이드·검수·발행·관리',
    img: '/images/onboarding_4.svg',
  },
  {
    name: '결과보고',
    sub: '대시보드와 보고서로 확인',
    owner: '결과 확인·다음 액션 결정',
    tble: '누적 성과·다음 회차 제안',
    img: '/images/onboarding_4.svg',
  },
];

const faqItems = [
  {
    q: '비용은 어떻게 정해지나요?',
    a: '채널 조합·회차·규모에 따라 달라집니다. 상담 시 운영 범위와 견적을 함께 안내드립니다.',
  },
  {
    q: '우리 업종에도 효과가 있을까요?',
    a: '식음·뷰티·이커머스·앱 등 폭넓은 업종을 운영해 왔습니다. 상담 시 업종별 사례를 보여드립니다.',
  },
  {
    q: '진행 기간은 얼마나 걸리나요?',
    a: '월 2회 회전이 기본, 첫 발행까지 평균 2~4주입니다. 규모에 따라 일정은 조정됩니다.',
  },
  {
    q: '당일 계약 프로모션은 어떻게 적용되나요?',
    a: '당일 계약 시 할인가와 숏폼 무상 제공 등 혜택이 적용됩니다. 패키지 구성 계약 기준이며, 조건은 상담 시 안내드립니다.',
  },
  {
    q: '성과는 어떻게 확인하나요?',
    a: '대시보드에서 진행 현황·등록 리뷰·유입·조회수 실시간 확인하며 종료 시 누적 보고서도 제공합니다.',
  },
];
</script>

<style scoped>
@keyframes marquee {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
}

.animate-marquee {
  display: flex;
  width: max-content;
  animation: marquee 35s linear infinite;
}

.animate-marquee:hover {
  animation-play-state: paused;
}

input::placeholder,
textarea::placeholder {
  color: #6b7280 !important;
  opacity: 1 !important;
}
</style>
